import React, { useCallback, useEffect, useId, useRef } from "react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import "../CustomTheme/customTheme.css";
import "./CustomCheckbox.css";

const defaultGetOptionLabel = (item) =>
  typeof item === "string" || typeof item === "number"
    ? String(item)
    : item?.name ?? item?.label ?? item?.title ?? String(item?.value ?? item?._id ?? "");

const defaultGetOptionValue = (item) =>
  typeof item === "string" || typeof item === "number"
    ? item
    : item?._id ?? item?.id ?? item?.value ?? item;

const Box = ({ id, checked, indeterminate, disabled, error, onChange, children, describedBy }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked;
  }, [indeterminate, checked]);

  return (
    <label
      htmlFor={id}
      className={`ccb-option${checked ? " ccb-checked" : ""}${disabled ? " ccb-disabled" : ""}${
        error ? " ccb-error" : ""
      }`}
    >
      <input
        ref={ref}
        id={id}
        type="checkbox"
        className="ccb-input"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy}
      />
      <span className="ccb-box" aria-hidden="true">
        <svg viewBox="0 0 16 16" className="ccb-tick">
          {indeterminate && !checked ? <path d="M4 8h8" /> : <path d="M3.5 8.5 6.5 11.5 12.5 4.5" />}
        </svg>
      </span>
      {children && <span className="ccb-text">{children}</span>}
    </label>
  );
};

/**
 * CustomCheckbox
 *
 * Single checkbox:
 *   <CustomCheckbox label="I agree" checked={agree} onChange={setAgree} error={err} />
 *   - label, description, checked, onChange(bool), indeterminate, disabled
 *
 * Group (pass `options`): value is an ARRAY of selected options
 *   <CustomCheckbox label="Days" options={days} value={selected} onChange={setSelected} />
 *   - label (group heading), options, value[], onChange(newArray),
 *     direction "row" | "column", getOptionLabel, getOptionValue
 *
 * Both: isRequired, hint, error, colClass, name, id, className, style
 */
const CustomCheckbox = ({
  label,
  description,
  options,
  value,
  checked,
  onChange,
  indeterminate,
  direction = "row",
  disabled = false,
  isRequired,
  hint,
  error,
  colClass,
  name,
  id,
  getOptionLabel,
  getOptionValue,
  className,
  style,
}) => {
  const autoId = useId();
  const baseId = id || name || `ccb-${autoId}`;
  const ids = getFieldIds(baseId);
  const describedBy = ids.describedBy(error, hint);

  const extractLabel = useCallback(
    (item) => (getOptionLabel ? getOptionLabel(item) : defaultGetOptionLabel(item)),
    [getOptionLabel]
  );
  const extractValue = useCallback(
    (item) => (getOptionValue ? getOptionValue(item) : defaultGetOptionValue(item)),
    [getOptionValue]
  );

  /* ---------- single checkbox ---------- */
  if (!Array.isArray(options)) {
    return (
      <CustomFormField
        htmlFor={baseId}
        hint={hint}
        error={error}
        colClass={colClass}
        className={className}
        style={style}
      >
        <Box
          id={baseId}
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          error={error}
          onChange={(next) => onChange?.(next)}
          describedBy={describedBy}
        >
          {(label || isRequired) && (
            <span>
              {label}
              {isRequired && <span className="ccb-required">*</span>}
            </span>
          )}
          {description && <span className="ccb-desc">{description}</span>}
        </Box>
      </CustomFormField>
    );
  }

  /* ---------- group ---------- */
  const selected = Array.isArray(value) ? value : [];
  const selectedKeys = selected.map(extractValue);

  const toggle = (item, isOn) => {
    const key = extractValue(item);
    const next = isOn
      ? [...selected, item]
      : selected.filter((s) => extractValue(s) !== key);
    onChange?.(next);
  };

  return (
    <CustomFormField
      label={label}
      isRequired={isRequired}
      fieldId={baseId}
      hint={hint}
      error={error}
      colClass={colClass}
      className={className}
      style={style}
    >
      <div className={`ccb-group ccb-${direction}`} role="group" aria-label={label}>
        {options.map((item, index) => {
          const key = extractValue(item);
          const optionId = `${baseId}-${index}`;
          return (
            <Box
              key={String(key ?? index)}
              id={optionId}
              checked={selectedKeys.includes(key)}
              disabled={disabled || item?.disabled}
              error={error}
              onChange={(isOn) => toggle(item, isOn)}
              describedBy={describedBy}
            >
              {extractLabel(item)}
            </Box>
          );
        })}
      </div>
    </CustomFormField>
  );
};

export default CustomCheckbox;
